import { createContext, ReactNode, useCallback, useEffect, useState } from "react";
import { AuthState } from "./authState";
import { User } from "./user";
import { config } from "../config";
import { TOKEN_KEY, USER_KEY, TOKEN_EXPIRY_KEY } from "./authConstants";

export interface AuthContextType extends AuthState {
    login: (email: string, password: string) => Promise<boolean>;
    logout: () => void;
    refreshToken: () => Promise<boolean>;
    updateDeviceToken: (deviceToken: string) => Promise<boolean>;
    // waitForTokenSync: (maxAttempts?: number, delay?: number) => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const getAuthHeader = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? `Bearer ${token}` : '';
};

export const tokenStorage = {
    get: () => localStorage.getItem(TOKEN_KEY),
    set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
    remove: () => localStorage.removeItem(TOKEN_KEY),

    getUser: (): User | null => {
        const userStr = localStorage.getItem(USER_KEY);
        return userStr ? JSON.parse(userStr) : null;
    },

    setUser: (user: User) => localStorage.setItem(USER_KEY, JSON.stringify(user)),
    removeUser: () => localStorage.removeItem(USER_KEY),

    getTokenExpiry: (): Date | null => {
        const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
        return expiry ? new Date(expiry) : null;
    },
    setTokenExpiry: (expiry: Date) => localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toISOString()),
    removeTokenExpiry: () => localStorage.removeItem(TOKEN_EXPIRY_KEY),

    clear: () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
    },

    isTokenExpired: (): boolean => {
        const expiry = tokenStorage.getTokenExpiry();
        return expiry ? new Date() >= expiry : true;
    }
};

const authAPI = {
    login: async (email: string, password: string) => {
        const response = await fetch(`${config.apiServerUrl}/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ Email: email, Password: password }),
        });

        if (!response.ok) {
            throw new Error('Login failed');
        }

        return response.json();
    },

    refreshToken: async (token: string) => {
        const response = await fetch(`${config.apiServerUrl}/auth/refreshToken`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error('Token refresh failed');
        }

        return response.json();
    },

    updateDeviceToken: async (token: string, deviceToken: string) => {
        const response = await fetch(`${config.apiServerUrl}/auth/updateDeviceToken`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ Token: deviceToken }),
        });

        if (!response.ok) {
            throw new Error('Device token update failed');
        }

        return response.json();
    },
    // waitForTokenSync: async (maxAttempts = 5, delay = 200) => {
    //     const token = localStorage.getItem(TOKEN_KEY);

    //     if (!token) {
    //         return false;
    //     }

    //     for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    //         try {
    //             const response = await fetch(`${config.pythonApiUrl}/auth/validate-token`, {
    //                 method: 'GET',
    //                 headers: {
    //                     'Authorization': `Bearer ${token}`,
    //                     'Content-Type': 'application/json'
    //                 }
    //             });

    //             if (response.ok) {
    //                 console.log(`Token sync successful on attempt ${attempt}`);
    //                 return true;
    //             }

    //             if (response.status !== 401) {
    //                 break;
    //             }

    //         } catch (error) {
    //             console.warn(`Token sync attempt ${attempt} failed:`, error);
    //         }

    //         if (attempt < maxAttempts) {
    //             await new Promise(resolve => setTimeout(resolve, delay * attempt));
    //         }
    //     }

    //     return false;
    // },

    logout: async (token: string) => {
        const response = await fetch(`${config.apiServerUrl}/auth/logout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error('Logout failed');
        }

        return response.json();
    }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [authState, setAuthState] = useState<AuthState>({
        user: null,
        token: null,
        loading: true,
        isAuthenticated: false,
        isSuperAdmin: false,
        canAccessInternalTools: false,
        isTokenSynced: false,
    });

    const logout = useCallback(async () => {
        const token = tokenStorage.get();

        tokenStorage.clear();
        setAuthState({
            user: null,
            token: null,
            loading: false,
            isAuthenticated: false,
            isSuperAdmin: false,
            canAccessInternalTools: false,
            isTokenSynced: false,
        });

        if (token) {
            try {
                await authAPI.logout(token);
            } catch (error) {
                console.error('Logout failed:', error);
            }
        }
    }, []);

    const refreshToken = useCallback(async (): Promise<boolean> => {
        try {
            const currentToken = tokenStorage.get();
            if (!currentToken) {
                logout();
                return false;
            }

            const response = await authAPI.refreshToken(currentToken);

            if (response.data && response.success) {
                const userData: User = {
                    userId: response.data.userId,
                    email: response.data.email,
                    firstName: response.data.firstName,
                    lastName: response.data.lastName,
                    timeZone: response.data.timeZone,
                    emailConfirmed: response.data.emailConfirmed,
                    isSuperAdmin: response.data.isSuperAdmin,
                    canAccessInternalTools: response.data.canAccessInternalTools,
                    roleData: response.data.roleData || [],
                    organizationId: response.data.organizationId,
                };

                tokenStorage.set(response.data.token);
                tokenStorage.setUser(userData);
                tokenStorage.setTokenExpiry(new Date(response.data.tokenExpiration));

                // const synced = await authAPI.waitForTokenSync(5, 200);

                setAuthState(prev => ({
                    ...prev,
                    user: userData,
                    token: response.data.token,
                    isSuperAdmin: userData.isSuperAdmin,
                    canAccessInternalTools: userData.canAccessInternalTools,
                    isTokenSynced: true,
                }));

                return true;
            }

            logout();
            return false;
        } catch (error) {
            logout();
            return false;
        }
    }, [logout]);

    useEffect(() => {
        const initializeAuth = async () => {
            const token = tokenStorage.get();
            const user = tokenStorage.getUser();

            if (token && user && !tokenStorage.isTokenExpired()) {
                // const synced = await authAPI.waitForTokenSync(3, 100);

                setAuthState({
                    user,
                    token,
                    loading: false,
                    isAuthenticated: true,
                    isSuperAdmin: user.isSuperAdmin,
                    canAccessInternalTools: user.canAccessInternalTools,
                    isTokenSynced: true,
                });
            } else {
                tokenStorage.clear();
                setAuthState({
                    user: null,
                    token: null,
                    loading: false,
                    isAuthenticated: false,
                    isSuperAdmin: false,
                    canAccessInternalTools: false,
                    isTokenSynced: false,
                });
            }
        };

        initializeAuth();
    }, []);

    useEffect(() => {
        if (!authState.token || !authState.isAuthenticated) return;

        const scheduleTokenRefresh = () => {
            const expiry = tokenStorage.getTokenExpiry();
            if (!expiry) return;

            const refreshTime = expiry.getTime() - Date.now() - (5 * 60 * 1000);

            if (refreshTime > 0) {
                const timeoutId = setTimeout(() => {
                    refreshToken();
                }, refreshTime);

                return () => clearTimeout(timeoutId);
            }
        };

        return scheduleTokenRefresh();
    }, [authState.token, authState.isAuthenticated, refreshToken]);

    const login = useCallback(async (email: string, password: string): Promise<boolean> => {
        try {
            setAuthState(prev => ({
                ...prev,
                isTokenSynced: false
            }));

            const response = await authAPI.login(email, password);

            if (response.data && response.success) {
                const userData: User = {
                    userId: response.data.userId,
                    email: response.data.email,
                    firstName: response.data.firstName,
                    lastName: response.data.lastName,
                    timeZone: response.data.timeZone,
                    emailConfirmed: response.data.emailConfirmed,
                    isSuperAdmin: response.data.isSuperAdmin,
                    canAccessInternalTools: response.data.canAccessInternalTools,
                    roleData: response.data.roleData || [],
                    organizationId: response.data.organizationId,
                };

                tokenStorage.set(response.data.token);
                tokenStorage.setUser(userData);
                tokenStorage.setTokenExpiry(new Date(response.data.tokenExpiration));

                setAuthState({
                    user: userData,
                    token: response.data.token,
                    loading: false,
                    isAuthenticated: true,
                    isSuperAdmin: userData.isSuperAdmin,
                    canAccessInternalTools: userData.canAccessInternalTools,
                    isTokenSynced: false,
                });

                // const synced = await authAPI.waitForTokenSync(5, 200);

                setAuthState(prev => ({
                    ...prev,
                    isTokenSynced: true
                }));

                return true;
            }
            return false;
        } catch (error) {
            console.error('Login error:', error);
            setAuthState(prev => ({
                ...prev,
                isTokenSynced: false
            }));
            return false;
        }
    }, []);

    const updateDeviceToken = useCallback(async (deviceToken: string): Promise<boolean> => {
        try {
            const currentToken = authState.token;
            if (!currentToken) return false;

            const response = await authAPI.updateDeviceToken(currentToken, deviceToken);
            return response.success || false;
        } catch (error) {
            console.error('Device token update error:', error);
            return false;
        }
    }, [authState.token]);

    const contextValue: AuthContextType = {
        ...authState,
        login,
        logout,
        refreshToken,
        updateDeviceToken,
        // waitForTokenSync: authAPI.waitForTokenSync,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};
